import { useMemo, useState } from 'react'
import { useGetMultipleQuery } from '../../services/historicalData/historicalDataApi'
import { GetMultipleCommand, GetMultipleResultDto } from '../../services/historicalData/types'
import {  ObjectSchemFromParentQueryArgs } from '../../services/objects'
import { IBaseCounter, schema as schemaCounter } from '../../automationObjects/Devices/BaseCounter'
import { TP_Building } from '../../automationObjects/Plants/Buildings/P_Building'
import { useGetPortfolioEnergyMeters } from '../useGetPortfolioEnergyMeters'
import { CounterMedium } from '../../automationObjects/Devices/CounterMedium'

interface useGetBuildingsEnergyOutput {
    count: number,
    items: any[] | undefined,
    average: number,
    error: any,
    isLoading: boolean,
    isSuccess: boolean,
    isFetching: boolean
}

export enum DisplayMode {
    EUR,
    CO2,
    KWH,
    M3
}


function useGetBuildingsEnergy(mode: DisplayMode, start: Date, end: Date, summarizeBy: string, byM2: boolean,  filterCounters?: (counter: IBaseCounter) => boolean, medium?: CounterMedium | undefined): useGetBuildingsEnergyOutput {

    const { items: buildingsAndCounters, isLoading: isLoadingC, error: errorC, isSuccess: isSuccessC } = useGetPortfolioEnergyMeters(medium);// useGetBuildingsCounters(buildings)
    const counterFilter = filterCounters ?? (v => v);
    const countersFlat = buildingsAndCounters?.flatMap((v) => v.counters.filter(counterFilter).map((c) => { return { objectId: c.id, resourceGroupId: c.resourceGroupId, propertyName: propertNameFromMode(mode) } }))
    const queryItems = countersFlat ?? [];
    // get the counters data
    const queryArgs: GetMultipleCommand = {
        start: start.toISOString(),
        end: end.toISOString(),
        items: queryItems
    }
    const hasCounters = queryArgs.items.length !== 0;
    const skip = !hasCounters;
    const { data: historicalData, isLoading, error, isSuccess, isFetching } = useGetMultipleQuery({ cmd: queryArgs, summarizeBy: summarizeBy }, { skip: skip });

    const finalData = useMemo(() => {
        const sum = summarizeCounters(historicalData?.map((entry, index) => {
            const building = findBuilding(buildingsAndCounters, entry.objectId);
            return { counter: entry, building: building, }
        }) || []);
        if (byM2)
            return sum.map((v) => { return { ...v, data: v.data.map((e) => { return { ...e, sumValue: e.sumValue / (v.building?.squereMeter || 9999999999999999) } }) } })
        return sum;
    }, [historicalData, buildingsAndCounters, byM2]);

    const average = useMemo(() => {
        const tmp: any[] = finalData.flatMap((ds) => ds.data.map((d) => d.sumValue));
        return tmp.reduce((partialSum, a) => partialSum + a, 0) / tmp.length;
    }, [finalData]);


    return {
        count: skip ? 0 : finalData?.length || 0,
        items: skip ? [] : finalData,
        average: skip ? 0 : average,
        error: error || errorC,
        isLoading: isLoading || isLoadingC,
        isSuccess: isSuccess && isSuccessC,
        isFetching: isFetching
    }
}

export default useGetBuildingsEnergy

function findBuilding(items: any[] | undefined, counterId: string): TP_Building | undefined {
    if (!items) return undefined;

    const flat = items.flatMap((ci: any) => { return ci.counters.map((c) => { return { buidling: ci.parent, counter: c } }) });
    return flat.find((c) => c.counter.id === counterId)?.buidling;
}

type JoinedItem = {
    counter: GetMultipleResultDto,
    building: TP_Building | undefined,
}

function getQueryArgs(b: TP_Building): ObjectSchemFromParentQueryArgs {
    return { objectId: b.id, resourceGroupId: b.resourceGroupId, schema: schemaCounter }
}

function propertNameFromMode(mode: DisplayMode) {
    switch (mode) {
        case DisplayMode.CO2:
            return "co2Consumption";
        case DisplayMode.EUR:
            return "actCostsConsumption";
        case DisplayMode.KWH:
            return "valueDelta";
        case DisplayMode.M3:
            return "valueDelta";
        default:
            throw new Error("not implemtened");
    }
}

function summarizeCounters(items: JoinedItem[]): any[] {
    const result = items.reduce((acc, curr) => {
        const buildingId = curr.building?.id || '';

        curr.counter.data.forEach((item) => {
            const timeStamp = item.timeStamp;
            const sumValue = item.sumValue;

            if (!acc[buildingId]) acc[buildingId] = { building: curr.building, values: {} };

            if (!acc[buildingId].values[timeStamp]) acc[buildingId].values[timeStamp] = sumValue;
            else acc[buildingId].values[timeStamp] += sumValue;
        });

        return acc;
    }, {});

    return Object.values(result).map((item: any) => { return { building: item.building, data: Object.entries(item.values).map((elm) => { return { timeStamp: elm[0], sumValue: elm[1] } }) } });
}

